// DuplicateMoveDialog Component - Duplicate or move content between workspaces
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Copy, Move, Loader2, FolderOutput, Check, Tag
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function DuplicateMoveDialog({ 
  open, 
  onOpenChange,
  mode = "duplicate", // "duplicate" or "move"
  contentType, // "course", "tp", "quiz", "assignment", "workspace"
  contentId,
  contentTitle,
  currentWorkspaceId,
  onSuccess
}) {
  const [loading, setLoading] = useState(false);
  const [workspaces, setWorkspaces] = useState([]);
  const [courses, setCourses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loadingWorkspaces, setLoadingWorkspaces] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  
  // Form state
  const [targetWorkspaceId, setTargetWorkspaceId] = useState('');
  const [targetCourseId, setTargetCourseId] = useState('');
  const [targetCategoryId, setTargetCategoryId] = useState('');
  const [newTitle, setNewTitle] = useState('');
  
  // Workspace duplication options
  const [includeCourses, setIncludeCourses] = useState(true);
  const [includeTps, setIncludeTps] = useState(true);
  const [includeQuizzes, setIncludeQuizzes] = useState(true);
  const [includeAssignments, setIncludeAssignments] = useState(true);
  const [includeParticipants, setIncludeParticipants] = useState(false);

  // Load workspaces when dialog opens
  useEffect(() => {
    if (open) {
      loadWorkspaces();
      setNewTitle(contentTitle ? `${contentTitle} (copie)` : '');
      setTargetWorkspaceId('');
      setTargetCourseId('');
      setTargetCategoryId('');
    }
  }, [open, contentTitle]);

  // Load courses when target workspace changes (for non-course move)
  useEffect(() => {
    if (targetWorkspaceId && contentType !== 'course' && contentType !== 'workspace') {
      loadCourses(targetWorkspaceId);
    } else {
      setCourses([]);
    }
  }, [targetWorkspaceId, contentType]);

  // Load categories when target workspace changes (for course duplicate)
  useEffect(() => {
    const wsId = targetWorkspaceId && targetWorkspaceId !== 'same' ? targetWorkspaceId : currentWorkspaceId;
    if (wsId && contentType === 'course') {
      loadCategories(wsId);
    } else {
      setCategories([]);
      setTargetCategoryId('');
    }
  }, [targetWorkspaceId, contentType, currentWorkspaceId]);

  const loadWorkspaces = async () => {
    setLoadingWorkspaces(true);
    try {
      const response = await fetch(`${API_URL}/api/duplicate/workspaces`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setWorkspaces(data);
      }
    } catch (error) {
      console.error('Failed to load workspaces:', error);
    } finally {
      setLoadingWorkspaces(false);
    }
  };

  const loadCourses = async (workspaceId) => {
    try {
      const response = await fetch(`${API_URL}/api/duplicate/workspaces/${workspaceId}/courses`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setCourses(data);
      }
    } catch (error) {
      console.error('Failed to load courses:', error);
    }
  };

  const loadCategories = async (workspaceId) => {
    setLoadingCategories(true);
    try {
      const response = await fetch(`${API_URL}/api/duplicate/workspaces/${workspaceId}/categories`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (error) {
      console.error('Failed to load categories:', error);
    } finally {
      setLoadingCategories(false);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    
    try {
      let url;
      let params = new URLSearchParams();
      
      if (mode === 'duplicate') {
        if (contentType === 'workspace') {
          url = `${API_URL}/api/duplicate/workspace/${contentId}`;
          if (newTitle) params.append('new_name', newTitle);
          params.append('include_courses', includeCourses);
          params.append('include_tps', includeTps);
          params.append('include_quizzes', includeQuizzes);
          params.append('include_assignments', includeAssignments);
          params.append('include_participants', includeParticipants);
        } else {
          url = `${API_URL}/api/duplicate/${contentType}/${contentId}`;
          if (targetWorkspaceId) params.append('target_workspace_id', targetWorkspaceId);
          if (newTitle) params.append('new_title', newTitle);
          if (targetCategoryId && targetCategoryId !== 'none' && contentType === 'course') params.append('target_category_id', targetCategoryId);
        }
      } else {
        // Move mode
        url = `${API_URL}/api/duplicate/move/${contentType}/${contentId}`;
        params.append('target_workspace_id', targetWorkspaceId);
        if (targetCourseId) params.append('target_course_id', targetCourseId);
      }
      
      const response = await fetch(`${url}?${params}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || (mode === 'duplicate' ? 'Dupliqué avec succès!' : 'Déplacé avec succès!'));
        onOpenChange(false);
        if (onSuccess) onSuccess(data);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Une erreur est survenue');
      }
    } catch (error) {
      console.error('Operation failed:', error);
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const getTitle = () => {
    if (mode === 'duplicate') {
      if (contentType === 'workspace') return 'Dupliquer l\'espace de travail';
      return `Dupliquer "${contentTitle}"`;
    }
    return `Déplacer "${contentTitle}"`;
  };

  const getDescription = () => {
    if (mode === 'duplicate') {
      if (contentType === 'workspace') {
        return 'Créez une copie complète de cet espace de travail avec tout son contenu.';
      }
      return 'Créez une copie de ce contenu dans le même espace ou un autre espace de travail.';
    }
    return 'Déplacez ce contenu vers un autre espace de travail.';
  };

  const getContentTypeLabel = () => {
    const labels = {
      course: 'cours',
      tp: 'TP',
      quiz: 'quiz',
      assignment: 'devoir',
      workspace: 'espace de travail'
    };
    return labels[contentType] || contentType;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-slate-900 border-slate-700">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            {mode === 'duplicate' ? (
              <Copy className="h-5 w-5 text-purple-500" />
            ) : (
              <FolderOutput className="h-5 w-5 text-blue-500" />
            )}
            {getTitle()}
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {getDescription()}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* New name/title */}
          {mode === 'duplicate' && (
            <div className="space-y-2">
              <Label className="text-slate-300">
                {contentType === 'workspace' ? 'Nouveau nom' : 'Nouveau titre'}
              </Label>
              <Input
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder={contentType === 'workspace' ? 'Nom de la copie' : 'Titre de la copie'}
                className="bg-slate-800 border-slate-700 text-white"
              />
            </div>
          )}

          {/* Target workspace selection (not for workspace duplication) */}
          {contentType !== 'workspace' && (
            <div className="space-y-2">
              <Label className="text-slate-300">
                {mode === 'duplicate' ? 'Espace cible (optionnel)' : 'Espace de destination'}
              </Label>
              <Select value={targetWorkspaceId} onValueChange={setTargetWorkspaceId}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                  <SelectValue placeholder={
                    loadingWorkspaces 
                      ? "Chargement..." 
                      : mode === 'duplicate' 
                        ? "Même espace (par défaut)" 
                        : "Sélectionner un espace"
                  } />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {mode === 'duplicate' && (
                    <SelectItem value="same" className="text-slate-300">
                      Même espace (copie locale)
                    </SelectItem>
                  )}
                  {workspaces
                    .filter(ws => mode === 'move' ? ws.id !== currentWorkspaceId : true)
                    .map(ws => (
                      <SelectItem 
                        key={ws.id} 
                        value={ws.id}
                        className="text-slate-300"
                      >
                        {ws.name}
                        {ws.id === currentWorkspaceId && ' (actuel)'}
                      </SelectItem>
                    ))
                  }
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Category selection (for course duplication) */}
          {mode === 'duplicate' && contentType === 'course' && (
            <div className="space-y-2">
              <Label className="text-slate-300 flex items-center gap-1.5">
                <Tag className="h-3.5 w-3.5" />
                Catégorie (optionnel)
              </Label>
              <Select value={targetCategoryId} onValueChange={setTargetCategoryId}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                  <SelectValue placeholder={
                    loadingCategories
                      ? "Chargement..."
                      : "Aucune catégorie"
                  } />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="none" className="text-slate-300">
                    Aucune catégorie
                  </SelectItem>
                  {categories.map(cat => (
                    <SelectItem 
                      key={cat.id} 
                      value={cat.id}
                      className="text-slate-300"
                    >
                      <span className="flex items-center gap-2">
                        {cat.color && (
                          <span 
                            className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0" 
                            style={{ backgroundColor: cat.color }} 
                          />
                        )}
                        {cat.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Target course selection (for tp, quiz, assignment in move mode) */}
          {mode === 'move' && contentType !== 'course' && contentType !== 'workspace' && targetWorkspaceId && (
            <div className="space-y-2">
              <Label className="text-slate-300">Rattacher à un cours (optionnel)</Label>
              <Select value={targetCourseId} onValueChange={setTargetCourseId}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                  <SelectValue placeholder="Aucun (non rattaché)" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="none" className="text-slate-300">
                    Aucun (non rattaché)
                  </SelectItem>
                  {courses.map(course => (
                    <SelectItem 
                      key={course.id} 
                      value={course.id}
                      className="text-slate-300"
                    >
                      {course.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Workspace duplication options */}
          {contentType === 'workspace' && mode === 'duplicate' && (
            <div className="space-y-3 pt-2">
              <Label className="text-slate-300 text-sm">Inclure dans la copie :</Label>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between p-2 rounded bg-slate-800/50">
                  <span className="text-sm text-slate-300">Cours</span>
                  <Switch checked={includeCourses} onCheckedChange={setIncludeCourses} />
                </div>
                
                <div className="flex items-center justify-between p-2 rounded bg-slate-800/50">
                  <span className="text-sm text-slate-300">TPs</span>
                  <Switch checked={includeTps} onCheckedChange={setIncludeTps} />
                </div>
                
                <div className="flex items-center justify-between p-2 rounded bg-slate-800/50">
                  <span className="text-sm text-slate-300">Quiz</span>
                  <Switch checked={includeQuizzes} onCheckedChange={setIncludeQuizzes} />
                </div>
                
                <div className="flex items-center justify-between p-2 rounded bg-slate-800/50">
                  <span className="text-sm text-slate-300">Devoirs</span>
                  <Switch checked={includeAssignments} onCheckedChange={setIncludeAssignments} />
                </div>
                
                <div className="flex items-center justify-between p-2 rounded bg-slate-800/50">
                  <span className="text-sm text-slate-300">Participants</span>
                  <Switch checked={includeParticipants} onCheckedChange={setIncludeParticipants} />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex justify-end gap-2 pt-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-slate-700 text-slate-300"
          >
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading || (mode === 'move' && !targetWorkspaceId)}
            className={mode === 'duplicate' ? 'bg-purple-600 hover:bg-purple-700' : 'bg-blue-600 hover:bg-blue-700'}
          >
            {loading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : mode === 'duplicate' ? (
              <Copy className="h-4 w-4 mr-2" />
            ) : (
              <Move className="h-4 w-4 mr-2" />
            )}
            {mode === 'duplicate' ? 'Dupliquer' : 'Déplacer'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Quick action buttons for use in menus/toolbars
export function DuplicateButton({ onClick, className = "" }) {
  return (
    <Button 
      variant="ghost" 
      size="sm" 
      onClick={onClick}
      className={`text-slate-400 hover:text-purple-400 ${className}`}
    >
      <Copy className="h-4 w-4" />
    </Button>
  );
}

export function MoveButton({ onClick, className = "" }) {
  return (
    <Button 
      variant="ghost" 
      size="sm" 
      onClick={onClick}
      className={`text-slate-400 hover:text-blue-400 ${className}`}
    >
      <FolderOutput className="h-4 w-4" />
    </Button>
  );
}
